// Implements: F-UIX-019
/**
 * The platform admin's view (UIX.8), split by the redesign (UX-11) into four sibling pages:
 * `/plataforma/totales`, `/plataforma/topes`, `/plataforma/modelos` and `/plataforma/estado`.
 * They sit behind `RequirePlatformAdmin`; `/admin` and `/plataforma` only redirect to the first
 * page the reader may open (`firstPlatformHref`, the sidebar's own model).
 *
 * Each page mounts only when the server answers its own action on the platform scope (UIX.5,
 * S12b-4): the totals on `usage_totals:read`, the ceilings on `usage_ceiling:manage`, the model
 * settings (F-GEN-036) on `model_settings:manage`, the status on `system_status:read`; a page whose
 * action is not answered is the not-found page and reads nothing. The notices (limits off, global
 * alert) sit under the title of every page when `system_status:read` is answered: one cached read
 * shared with the status page.
 */

import { useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { Navigate } from "react-router";

import { CeilingsContainer } from "../../modules/admin/containers/CeilingsContainer";
import { FlowsContainer } from "../../modules/admin/containers/FlowsContainer";
import { ModelSettingsContainer } from "../../modules/admin/containers/ModelSettingsContainer";
import { PlatformLatencyContainer } from "../../modules/admin/containers/PlatformLatencyContainer";
import { PlatformNoticesContainer } from "../../modules/admin/containers/PlatformNoticesContainer";
import { PlatformTotalsContainer } from "../../modules/admin/containers/PlatformTotalsContainer";
import { SystemStatusContainer } from "../../modules/admin/containers/SystemStatusContainer";
import { platformAllows } from "../../modules/admin/model/platformAccess";
import type { PlatformAction } from "../../modules/admin/model/platformAccess";
import { useSession } from "../../shared/api/session";
import { useTranslation } from "../../shared/i18n";
import { PageHeader } from "../../shared/ui/molecules/PageHeader";
import { Tabs, tabPanelProps } from "../../shared/ui/molecules/Tabs";
import type { BackofficeScope } from "../../types/api";
import { firstPlatformHref } from "../layout/navigation";
import type { NavLabelKey } from "../layout/navigation";
import { NotFoundPage } from "./NotFoundPage";
import styles from "./PlatformAdminPage.module.css";

const NO_SCOPES: readonly BackofficeScope[] = [];

/** The sentence under each page title (none takes a parameter). */
type SubtitleKey =
  | "admin.page.totals.subtitle"
  | "admin.ceilings.intro"
  | "admin.page.models.subtitle"
  | "admin.page.status.subtitle";

function useScopes(): readonly BackofficeScope[] {
  const { state } = useSession();
  return state.status === "authenticated" ? state.view.scopes : NO_SCOPES;
}

interface PlatformPageProps {
  /** The action the server must answer for this page to exist. */
  readonly action: PlatformAction;
  /** The page title: the sidebar item's own label, so the `h1` and the breadcrumb agree. */
  readonly titleKey: NavLabelKey;
  readonly subtitleKey: SubtitleKey;
  readonly children: ReactNode;
}

/** One platform page: its title, the notices, then its content; the not-found page without its action. */
function PlatformPage({ action, titleKey, subtitleKey, children }: PlatformPageProps): ReactElement {
  const { t } = useTranslation();
  const scopes = useScopes();
  if (!platformAllows(scopes, action)) return <NotFoundPage />;
  return (
    <section className={styles.page}>
      <PageHeader title={t(titleKey)} subtitle={t(subtitleKey)} />
      {platformAllows(scopes, "system_status:read") ? <PlatformNoticesContainer /> : null}
      <div className={styles.content} data-platform-content="">
        {children}
      </div>
    </section>
  );
}

/** `/admin` and `/plataforma`: the first platform page the reader may open (the guard ensured one). */
export function PlatformAdminRedirect(): ReactElement {
  const href = firstPlatformHref(useScopes());
  return href === null ? <NotFoundPage /> : <Navigate to={href} replace />;
}

export function PlatformTotalsPage(): ReactElement {
  return (
    <PlatformPage action="usage_totals:read" titleKey="app.nav.platformTotals" subtitleKey="admin.page.totals.subtitle">
      <PlatformTotalsContainer />
    </PlatformPage>
  );
}

export function PlatformCeilingsPage(): ReactElement {
  return (
    <PlatformPage action="usage_ceiling:manage" titleKey="app.nav.platformCeilings" subtitleKey="admin.ceilings.intro">
      <CeilingsContainer />
    </PlatformPage>
  );
}

/**
 * The two tabs of Modelos (mockup v3): the model settings, and the flows P9 lists. The settings
 * stay mounted (hidden) while Flujos is shown, so unsaved edits survive a look at the flows; the
 * flows mount the first time their tab is chosen and then stay.
 */
type ModelsTab = "settings" | "flows";

const MODELS_TABS = "platform-models";

export function PlatformModelsPage(): ReactElement {
  const { t } = useTranslation();
  const [tab, setTab] = useState<ModelsTab>("settings");
  const [flowsShown, setFlowsShown] = useState(false);
  return (
    <PlatformPage action="model_settings:manage" titleKey="app.nav.platformModels" subtitleKey="admin.page.models.subtitle">
      <Tabs
        label={t("admin.models.tabs.label")}
        idBase={MODELS_TABS}
        items={[
          { id: "settings", label: t("admin.models.tab.settings") },
          { id: "flows", label: t("admin.models.tab.flows") },
        ]}
        selected={tab}
        onSelect={(id) => {
          setTab(id === "flows" ? "flows" : "settings");
          if (id === "flows") setFlowsShown(true);
        }}
      />
      <div {...tabPanelProps(MODELS_TABS, "settings")} hidden={tab !== "settings"}>
        <ModelSettingsContainer />
      </div>
      <div {...tabPanelProps(MODELS_TABS, "flows")} hidden={tab !== "flows"}>
        {flowsShown ? <FlowsContainer /> : null}
      </div>
    </PlatformPage>
  );
}

export function PlatformStatusPage(): ReactElement {
  return (
    <PlatformPage action="system_status:read" titleKey="app.nav.platformStatus" subtitleKey="admin.page.status.subtitle">
      <SystemStatusContainer />
      <PlatformLatencyContainer />
    </PlatformPage>
  );
}
